/* =====================================================
   Black Build Project
   Product Image Viewer
===================================================== */

(() => {
  'use strict';

  const SELECTORS = {
    productGallery: '.product__media-list',
    mediaItem: '.product__media-item',
    modalOpener: 'modal-opener',
    image: 'img'
  };

  const state = {
    images: [],
    currentIndex: 0,
    viewer: null,
    viewerImage: null,
    counter: null,
    touchStartX: 0,
    touchStartY: 0,
    previousFocus: null
  };

  /**
   * Select the largest candidate from an image srcset.
   */
  function getLargestImageUrl(image) {
    const srcset = image.getAttribute('srcset');

    if (!srcset) {
      return image.currentSrc || image.src || '';
    }

    const candidates = srcset
      .split(',')
      .map((candidate) => {
        const parts = candidate.trim().split(/\s+/);
        const url = parts[0];
        const width = parseInt(parts[1], 10) || 0;

        return { url, width };
      })
      .filter((candidate) => candidate.url)
      .sort((a, b) => b.width - a.width);

    return candidates[0]?.url || image.currentSrc || image.src || '';
  }

  /**
   * Return the media ID used by Dawn.
   */
  function getMediaId(element) {
    const mediaItem = element.closest(SELECTORS.mediaItem);
    const opener = element.closest(SELECTORS.modalOpener);

    return (
      mediaItem?.dataset.mediaId ||
      opener?.dataset.mediaId ||
      opener?.getAttribute('data-media-id') ||
      ''
    );
  }

  /**
   * Read product images already rendered by Dawn.
   * Video and 3D media are ignored.
   */
  function collectProductImages() {
    const gallery = document.querySelector(SELECTORS.productGallery);

    if (!gallery) {
      return [];
    }

    const results = [];
    const usedUrls = new Set();

    gallery.querySelectorAll(SELECTORS.mediaItem).forEach((item) => {
      const image = item.querySelector(SELECTORS.image);

      if (!image) {
        return;
      }

      const url = getLargestImageUrl(image);

      if (!url || usedUrls.has(url)) {
        return;
      }

      usedUrls.add(url);

      results.push({
        id: item.dataset.mediaId || getMediaId(item),
        url,
        alt: image.alt || ''
      });
    });

    return results;
  }

  function createButton(className, label) {
    const button = document.createElement('button');

    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);

    return button;
  }

  function createViewer() {
    if (document.querySelector('[data-bbp-viewer]')) {
      return;
    }

    const viewer = document.createElement('div');
    viewer.className = 'bbp-viewer';
    viewer.dataset.bbpViewer = '';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', '商品画像');

    const stage = document.createElement('div');
    stage.className = 'bbp-viewer__stage';

    const image = document.createElement('img');
    image.className = 'bbp-viewer__image';
    image.alt = '';
    image.draggable = false;

    const loader = document.createElement('span');
    loader.className = 'bbp-viewer__loader';
    loader.setAttribute('aria-hidden', 'true');

    const closeButton = createButton(
      'bbp-viewer__close',
      '画像表示を閉じる'
    );

    const previousButton = createButton(
      'bbp-viewer__arrow bbp-viewer__arrow--previous',
      '前の画像'
    );

    const nextButton = createButton(
      'bbp-viewer__arrow bbp-viewer__arrow--next',
      '次の画像'
    );

    const counter = document.createElement('div');
    counter.className = 'bbp-viewer__counter';
    counter.setAttribute('aria-live', 'polite');

    stage.append(
      image,
      loader,
      closeButton,
      previousButton,
      nextButton,
      counter
    );

    viewer.append(stage);
    document.body.append(viewer);

    state.viewer = viewer;
    state.viewerImage = image;
    state.counter = counter;

    closeButton.addEventListener('click', closeViewer);
    previousButton.addEventListener('click', showPrevious);
    nextButton.addEventListener('click', showNext);

    viewer.addEventListener('click', (event) => {
      if (event.target === viewer || event.target === stage) {
        closeViewer();
      }
    });

    viewer.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });

    viewer.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
  }

  function preloadImage(url) {
    if (!url) {
      return;
    }

    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }

  /**
   * Load all product images in the browser cache.
   */
  function preloadAllImages() {
    state.images.forEach((item) => {
      preloadImage(item.url);
    });
  }

  function preloadNearbyImages(index) {
    if (state.images.length < 2) {
      return;
    }

    const previousIndex =
      (index - 1 + state.images.length) % state.images.length;

    const nextIndex =
      (index + 1) % state.images.length;

    preloadImage(state.images[previousIndex]?.url);
    preloadImage(state.images[nextIndex]?.url);
  }

  function updateCounter() {
    if (!state.counter) {
      return;
    }

    state.counter.textContent =
      `${state.currentIndex + 1} / ${state.images.length}`;
  }

  function displayImage(index, immediate = false) {
    if (!state.viewerImage || !state.viewer || !state.images.length) {
      return;
    }

    const normalizedIndex =
      (index + state.images.length) % state.images.length;

    const item = state.images[normalizedIndex];

    state.currentIndex = normalizedIndex;

    state.viewer.classList.add('is-loading');
    state.viewerImage.classList.remove('is-visible');

    const revealImage = () => {
      state.viewer?.classList.remove('is-loading');

      requestAnimationFrame(() => {
        state.viewerImage?.classList.add('is-visible');
      });
    };

    if (state.viewerImage.src === item.url && state.viewerImage.complete) {
      state.viewerImage.alt = item.alt;
      revealImage();
    } else {
      state.viewerImage.onload = revealImage;

      state.viewerImage.onerror = () => {
        state.viewer?.classList.remove('is-loading');
      };

      state.viewerImage.alt = item.alt;
      state.viewerImage.src = item.url;

      if (state.viewerImage.complete) {
        revealImage();
      }
    }

    if (immediate) {
      state.viewerImage.classList.add('is-visible');
    }

    updateCounter();
    preloadNearbyImages(normalizedIndex);
  }

  function openViewer(index) {
    if (!state.viewer) {
      createViewer();
    }

    if (!state.viewer) {
      return;
    }

    state.previousFocus = document.activeElement;

    displayImage(index, true);

    document.documentElement.classList.add('bbp-viewer-open');
    document.body.classList.add('bbp-viewer-open');

    state.viewer.classList.add('is-open');

    const closeButton = state.viewer.querySelector('.bbp-viewer__close');
    closeButton?.focus({ preventScroll: true });
  }

  function closeViewer() {
    if (!state.viewer) {
      return;
    }

    state.viewer.classList.remove('is-open', 'is-loading');

    document.documentElement.classList.remove('bbp-viewer-open');
    document.body.classList.remove('bbp-viewer-open');

    if (state.previousFocus instanceof HTMLElement) {
      state.previousFocus.focus({ preventScroll: true });
    }
  }

  function showPrevious() {
    displayImage(state.currentIndex - 1);
  }

  function showNext() {
    displayImage(state.currentIndex + 1);
  }

  function handleTouchStart(event) {
    const touch = event.changedTouches[0];

    state.touchStartX = touch.clientX;
    state.touchStartY = touch.clientY;
  }

  function handleTouchEnd(event) {
    const touch = event.changedTouches[0];

    const differenceX = touch.clientX - state.touchStartX;
    const differenceY = touch.clientY - state.touchStartY;

    if (
      Math.abs(differenceX) < 45 ||
      Math.abs(differenceX) < Math.abs(differenceY)
    ) {
      return;
    }

    if (differenceX > 0) {
      showPrevious();
    } else {
      showNext();
    }
  }

  function findImageIndex(opener) {
    const mediaId = getMediaId(opener);

    if (mediaId) {
      const indexById = state.images.findIndex(
        (item) => item.id === mediaId
      );

      if (indexById >= 0) {
        return indexById;
      }
    }

    const clickedImage = opener.querySelector('img');

    if (!clickedImage) {
      return 0;
    }

    const clickedUrl = getLargestImageUrl(clickedImage);

    const indexByUrl = state.images.findIndex(
      (item) => item.url === clickedUrl
    );

    return indexByUrl >= 0 ? indexByUrl : 0;
  }

  /**
   * Intercept Dawn image modal openers.
   * Capture phase prevents Dawn's default modal from opening.
   */
  function handleProductImageClick(event) {
    const opener = event.target.closest(SELECTORS.modalOpener);

    if (!opener) {
      return;
    }

    const image = opener.querySelector('img');

    /*
      Do not replace video or 3D-model interactions.
    */
    if (!image) {
      return;
    }

    if (!opener.closest(SELECTORS.productGallery)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const index = findImageIndex(opener);
    openViewer(index);
  }

  function handleKeyboard(event) {
    if (!state.viewer?.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape') {
      closeViewer();
    }

    if (event.key === 'ArrowLeft') {
      showPrevious();
    }

    if (event.key === 'ArrowRight') {
      showNext();
    }
  }

  function initialize() {
    state.images = collectProductImages();

    if (!state.images.length) {
      return;
    }

    createViewer();

    /*
      Start preloading after the main page has rendered.
      requestIdleCallback is used when available.
    */
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadAllImages, {
        timeout: 2500
      });
    } else {
      window.setTimeout(preloadAllImages, 1000);
    }

    document.addEventListener(
      'click',
      handleProductImageClick,
      true
    );

    document.addEventListener('keydown', handleKeyboard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true
    });
  } else {
    initialize();
  }
})();